import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';

import { IconCollection } from '@components/icons/iconCollection';

import { RatingScale } from './RatingScale';

import type { FeedbackDialogViewProperties } from './FeedbackDialogView.types';

/**
 * FeedbackDialogView - Presentational component for feedback modal
 * Composes RatingScale + comment field inside a Dialog
 */
export const FeedbackDialogView: React.FC<FeedbackDialogViewProperties> = ({
  open,
  rating,
  comment,
  isSubmitting,
  onRatingChange,
  onCommentChange,
  onSubmit,
  onSkip,
  dataTest = 'feedback_dialog',
}) => {
  return (
    <Dialog
      open={open}
      maxWidth='sm'
      fullWidth
      data-testid={dataTest}
      disableRestoreFocus
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant='h6'
            component='span'
            data-testid={`${dataTest}_title`}
            sx={{
              fontWeight: 700
            }}
          >
            Оцените результат анализа
          </Typography>
          <IconButton
            onClick={onSkip}
            size='small'
            sx={{ color: 'text.disabled' }}
          >
            <FontAwesomeIcon icon={IconCollection.xmark} />
          </IconButton>
        </Box>
        <Typography
          variant='body2'
          data-testid={`${dataTest}_subtitle`}
          sx={{
            color: 'text.secondary',
            mt: 0.5
          }}>
          Ваша оценка поможет нам улучшить качество анализа
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        <Box sx={{ mb: 2.5 }}>
          <RatingScale
            value={rating}
            onChange={onRatingChange}
            dataTest={`${dataTest}_rating`}
          />
        </Box>

        <Box>
          <Typography
            variant='subtitle2'
            sx={{
              fontWeight: 600,
              mb: 1
            }}>
            Комментарий
          </Typography>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder='Что можно улучшить? (необязательно)'
            value={comment}
            onChange={event => onCommentChange(event.target.value)}
            data-testid={`${dataTest}_comment`}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button
          onClick={onSkip}
          disabled={isSubmitting}
          color='inherit'
          sx={{ color: 'text.secondary', textTransform: 'none' }}
          data-testid={`${dataTest}_skip`}
        >
          Пропустить
        </Button>
        <Button
          variant='contained'
          onClick={rating === undefined ? undefined : () => onSubmit(rating)}
          disabled={rating === undefined || isSubmitting}
          sx={{ textTransform: 'none', px: 3 }}
          data-testid={`${dataTest}_submit`}
        >
          {isSubmitting ? 'Отправка...' : 'Отправить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
